interface CustomWindow extends Window {
  MSStream?: unknown;
}

/**
 * Detects if the current device is an iOS device
 * @returns boolean indicating if the device is iOS
 */
export const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as CustomWindow).MSStream;
};

/**
 * Detects if the current device is Safari on iOS
 * @returns boolean indicating if the device is Safari on iOS
 */
export const isSafariMobile = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent;
  const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent) && !(window as CustomWindow).MSStream;
  
  if (!isIOSDevice) return false;
  
  // Check if it's Safari (not Chrome, Firefox, Brave, or other browsers on iOS)
  // Safari on iOS doesn't have "CriOS" (Chrome), "FxiOS" (Firefox), "Brave/" (Brave), or other browser identifiers
  const isSafari = !/(CriOS|FxiOS|EdgiOS|OPiOS|Brave\/|mercury|MQQBrowser|UCBrowser)/i.test(userAgent) && 
                   /Safari/i.test(userAgent);
  
  return isSafari;
};

/**
 * Detects if the current device is Android mobile
 * @returns boolean indicating if the device is Android mobile
 */
export const isAndroidMobile = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /Android/i.test(navigator.userAgent) && isMobileDevice();
};

/**
 * Detects if the current device is a mobile device
 * @returns boolean indicating if the device is mobile
 */
export const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = window.navigator.userAgent || window.navigator.vendor;
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  
  return mobileRegex.test(userAgent);
};

/**
 * Network connection information
 */
export interface NetworkInfo {
  effectiveType: '4g' | '3g' | '2g' | 'slow-2g' | 'unknown';
  isLikelyFast: boolean;
  isLikelyCellular: boolean;
}

/**
 * Gets network connection information using Network Information API
 * @returns NetworkInfo object with connection details
 */
export const getNetworkInfo = (): NetworkInfo => {
  if (typeof window === 'undefined') {
    return { effectiveType: 'unknown', isLikelyFast: false, isLikelyCellular: false };
  }

  // Try to access Network Information API
  const connection = (navigator as any).connection || 
                    (navigator as any).mozConnection || 
                    (navigator as any).webkitConnection;

  if (!connection) {
    return { 
      effectiveType: 'unknown', 
      isLikelyFast: false, 
      isLikelyCellular: isMobileDevice() // Mobile users might be on cellular even if API unavailable
    };
  }

  const effectiveType = connection.effectiveType || 'unknown';
  const isLikelyFast = effectiveType === '4g';
  
  // On mobile devices, we can't reliably distinguish WiFi from cellular
  // So we assume mobile users might be on cellular unless we have strong indicators otherwise
  // Conservative approach: warn mobile users about data usage
  const isLikelyCellular = isMobileDevice();

  return {
    effectiveType,
    isLikelyFast,
    isLikelyCellular
  };
};

/**
 * Checks if data warnings should be hidden for this session
 * @returns boolean indicating if data warnings should be hidden
 */
export const shouldHideDataWarnings = (): boolean => {
  if (typeof window === 'undefined') return false;
  return sessionStorage.getItem('hideDataWarnings') === 'true';
};

/**
 * Checks if a download size might be problematic for mobile
 * @param sizeInBytes - Size of download in bytes
 * @returns Object with warning recommendations
 */
export const getMobileDownloadWarnings = (sizeInBytes: number) => {
  const sizeMB = sizeInBytes / (1024 * 1024);
  const networkInfo = getNetworkInfo();
  const isMobile = isMobileDevice();
  const hideDataWarnings = shouldHideDataWarnings();
  
  return {
    isMobile,
    sizeMB: Math.round(sizeMB),
    shouldWarnDataUsage: isMobile && sizeMB > 50 && !hideDataWarnings, // 50MB threshold - warn about potential data usage (unless hidden)
    shouldWarnLargeSize: isMobile && sizeMB > 200, // 200MB threshold for mobile failure risk (always show)
    networkInfo
  };
}; 